<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action :icon="AddIcon" :label="$t('pages.advertisements.modal.new.buttonText')" @click="openAdvertisementModal = true" />
    <export
      url="/advertisements/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.advertisements')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <AdvertisementModal v-model:open="openAdvertisementModal" v-model:advertisement="editedAdvertisement" @saved="refetch" />
  <div class="main-area h-full">
    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t("pages.advertisements.tableHeader") }}
      </div>

      <div class="flex flex-col">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :disable-frontend-sorting="true"
          :actions="actions"
        />
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { format } from 'date-fns';
import { useTableFields } from '@hooks/table-configuration';
import AdvertisementModal from './AdvertisementModal.vue';
import type {
  HydraCollection, Advertisement, AdvertisementPlace, Region,
} from '@/entities/Entities';
import { useQueryRef } from '@/hooks/url';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);
const filter = useQueryRef('filter', {});

const openAdvertisementModal = ref(false);
const editedAdvertisement = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteAdvertisement, { loading: deleting }] = useDelete(t('pages.advertisements.actions.deleteWarning'));

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.advertisements.actions.edit'),
    action: (item: Advertisement) => {
      openAdvertisementModal.value = true;
      editedAdvertisement.value = { ...item };
    },
  },
  {
    title: t('pages.advertisements.actions.delete'),
    action: async (item: Advertisement) => {
      if (await deleteAdvertisement(`/advertisements/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

// TODO
const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.advertisementList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'name',
    title: t('advertisement.name'),
    group: t('pages.advertisementList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        label: t('advertisement.name'),
      },
    },
  },
  {
    key: 'company.name',
    title: t('advertisement.company'),
    group: t('pages.advertisementList.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'company',
      type: 'dropdown',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>(
          '/companies',
          {
            sort: {
              sortKey: 'name',
              sortDirection: 'ASC',
            },
          },
          (value) => ({
            label: value.name,
            value: value.id,
          }),
        ),
        label: t('advertisement.company'),
      },
    },
  },
  {
    key: 'showInApp',
    title: t('advertisement.showInApp'),
    group: t('pages.advertisementList.fieldGroups.general'),
    visible: true,
    formatter: (value: boolean) => (value ? t('yes') : t('no')),
    filter: {
      type: 'dropdown',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        options: [
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' },
        ],
        label: t('advertisement.showInApp'),
      },
    },
  },
  {
    key: 'showInForBusiness',
    title: t('advertisement.showInForBusiness'),
    group: t('pages.advertisementList.fieldGroups.general'),
    visible: true,
    formatter: (value: boolean) => (value ? t('yes') : t('no')),
    filter: {
      type: 'dropdown',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        options: [
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' },
        ],
        label: t('advertisement.showInForBusiness'),
      },
    },
  },
  {
    key: 'validityStart',
    title: t('advertisement.validityStart'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.advertisementList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        label: t('advertisement.validityStart'),
      },
    },
  },
  {
    key: 'validityEnd',
    title: t('advertisement.validityEnd'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.advertisementList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        label: t('advertisement.validityEnd'),
      },
    },
  },
  {
    key: 'places',
    title: t('advertisement.places'),
    group: t('pages.advertisementList.fieldGroups.general'),
    visible: true,
    sortKey: 'places.spot',
    formatter: (v: AdvertisementPlace[]) => {
      if (v && v.length === 0) return '-';
      return v
        .sort((a, b) => a.spot.localeCompare(b.spot))
        .map((place) => t(`advertisement.spots.${place.spot}`))
        .join(', ');
    },
    filter: {
      key: 'places.spot',
      type: 'tags',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        closeOnSelect: false,
        options: enumOptionsProvider('/enums/AdvertisementSpot', (v) => ({
          value: v,
          label: t(`advertisement.spots.${v}`),
        })),
        label: t('advertisement.places'),
      },
    },
  },
  {
    key: 'regions',
    title: t('advertisement.regions'),
    group: t('pages.advertisementList.fieldGroups.general'),
    visible: true,
    sortKey: 'regions.name',
    formatter: (v: Region[]) => v.map((r) => r.name).join(', '),
    filter: {
      key: 'regions',
      type: 'tags',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>(
          '/regions',
          {
            sort: {
              sortKey: 'name',
              sortDirection: 'ASC',
            },
          },
          (value) => ({
            label: value.name,
            value: value.id,
          }),
        ),
        label: t('advertisement.regions'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    group: t('pages.advertisementList.fieldGroups.general'),
    visible: true,
    sortable: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    filter: {
      type: 'datetime',
      group: t('pages.advertisementList.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
  {
    key: 'counter',
    title: t('advertisement.counter'),
    group: t('pages.advertisementList.fieldGroups.counters'),
    visible: true,
    sortable: true,
    formatter: (v: number) => (v ? v.toString() : '-'),
    filter: {
      type: 'range',
      group: t('pages.advertisementList.filterGroups.counters'),
      options: {
        label: t('advertisement.counter'),
      },
    },
  },
  // 20250603-NH: impressionCount deactivated for now as it contains the same info as counter
  // {
  //   key: 'impressionCount',
  //   title: t('advertisement.impressionCount'),
  //   group: t('pages.advertisementList.fieldGroups.counters'),
  //   visible: true,
  //   sortable: false,
  //   formatter: (v: number) => (v ? v.toString() : '-'),
  //   filter: {
  //     type: 'range',
  //     group: t('pages.advertisementList.filterGroups.counters'),
  //     options: {
  //       label: t('advertisement.impressionCount'),
  //     },
  //   },
  // },
  {
    key: 'uniqueImpressionCount',
    title: t('advertisement.uniqueImpressionCount'),
    group: t('pages.advertisementList.fieldGroups.counters'),
    visible: true,
    sortable: false,
    formatter: (v: number) => (v ? v.toString() : '-'),
    // 20250603-NH: filtering of uniqueImpressionCount deactivated as per WOR-2584.
    // filter: {
    //   type: 'range',
    //   group: t('pages.advertisementList.filterGroups.counters'),
    //   options: {
    //     label: t('advertisement.uniqueImpressionCount'),
    //   },
    // },
  },
  {
    key: 'clickCounter',
    title: t('advertisement.clickCounter'),
    group: t('pages.advertisementList.fieldGroups.counters'),
    visible: true,
    sortable: true,
    formatter: (v: number) => (v ? v.toString() : '-'),
    filter: {
      type: 'range',
      group: t('pages.advertisementList.filterGroups.counters'),
      options: {
        label: t('advertisement.clickCounter'),
      },
    },
  },
  {
    key: 'uniqueClickedImpressionCount',
    title: t('advertisement.uniqueClickedImpressionCount'),
    group: t('pages.advertisementList.fieldGroups.counters'),
    visible: true,
    sortable: false,
    formatter: (v: number) => (v ? v.toString() : '-'),
    // 20250603-NH: filtering of uniqueClickedImpressionCount deactivated as per WOR-2584.
    // filter: {
    //   type: 'range',
    //   group: t('pages.advertisementList.filterGroups.counters'),
    //   options: {
    //     label: t('advertisement.uniqueClickedImpressionCount'),
    //   },
    // },
  },
];

const visibleColumns = useTableFields(columns);

const { data: items, loading: loadingTable, refetch } = useProvider<HydraCollection<Advertisement>>('/advertisements', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);
</script>
